import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, CardBody, Button, Input, Spinner } from "reactstrap";
import { AiOutlineArrowLeft, AiOutlineSend } from "react-icons/ai";
import axios from "axios";
import { toast } from "react-toastify";

const FeedbackChat = ({ feedbackId, onBack }) => {
  const navigate = useNavigate();
  const { AuthReducer: { auth } } = useSelector((state) => state);
  
  const [feedback, setFeedback] = useState(null);
  const [replies, setReplies] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchFeedbackDetails();
    fetchReplies();
  }, [feedbackId]);

  useEffect(() => {
    scrollToBottom();
  }, [replies]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchFeedbackDetails = async () => {
    try {
      const response = await axios.get(`/api/admin/v1/feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setFeedback(response.data.data);
    } catch (error) {
      toast.error("Error fetching feedback details");
    }
  };

  const fetchReplies = async () => {
    try {
      const response = await axios.get(`/api/admin/v1/feedback/${feedbackId}/reply`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setReplies(response.data.data || []);
    } catch (error) {
      toast.error("Error fetching replies");
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async () => {
    if (!newMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSending(true);
    try {
      await axios.put(`/api/admin/v1/feedback/${feedbackId}/reply`, 
        { message: newMessage },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
      
      setNewMessage("");
      fetchReplies();
      toast.success("Reply sent successfully");
    } catch (error) {
      toast.error("Error sending reply");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card>
            <CardBody>
              {/* Header */}
              <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                <Button 
                  color="link" 
                  className="p-0 me-3"
                  onClick={() => onBack ? onBack() : navigate(-1)}
                >
                  <AiOutlineArrowLeft size={20} />
                </Button>
                <div>
                  <h5 className="mb-1">{feedback?.subject}</h5>
                  <small className="text-muted">
                    {feedback?.name} ({feedback?.email}) • {feedback?.category}
                  </small>
                </div>
              </div>

              {/* Chat Container */}
              <div 
                className="chat-container mb-4" 
                style={{
                  height: '500px',
                  overflowY: 'auto',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa'
                }}
              >
                {/* Original Message */}
                <div className="d-flex justify-content-start mb-3">
                  <div 
                    className="message-bubble user-message"
                    style={{
                      maxWidth: '70%',
                      padding: '12px 16px',
                      borderRadius: '18px 18px 18px 4px',
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #bbdefb'
                    }}
                  >
                    <div className="message-header mb-1">
                      <small className="fw-bold text-primary">{feedback?.name}</small>
                      <small className="text-muted ms-2">
                        {new Date(feedback?.createdAt).toLocaleString()}
                      </small>
                    </div>
                    <div className="message-content">{feedback?.message}</div>
                  </div>
                </div>

                {/* Replies */}
                {replies.map((reply, index) => (
                  <div 
                    key={index} 
                    className={`d-flex mb-3 ${reply.userType === 'admin' ? 'justify-content-end' : 'justify-content-start'}`}
                  >
                    <div 
                      className={`message-bubble ${reply.userType === 'admin' ? 'admin-message' : 'user-message'}`}
                      style={{
                        maxWidth: '70%',
                        padding: '12px 16px',
                        borderRadius: reply.userType === 'admin' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        backgroundColor: reply.userType === 'admin' ? '#007bff' : '#e3f2fd',
                        color: reply.userType === 'admin' ? 'white' : '#333',
                        border: reply.userType === 'admin' ? 'none' : '1px solid #bbdefb'
                      }}
                    >
                      <div className="message-header mb-1">
                        <small 
                          className={`fw-bold ${reply.userType === 'admin' ? 'text-light' : 'text-primary'}`}
                          style={{ opacity: 0.9 }}
                        >
                          {reply.userName}
                        </small>
                        <small 
                          className={reply.userType === 'admin' ? 'text-light' : 'text-muted'} 
                          style={{ opacity: 0.7 }}
                        >
                          {' • '}{new Date(reply.createdAt).toLocaleString()}
                        </small>
                      </div>
                      <div className="message-content">{reply.message}</div>
                    </div>
                  </div>
                ))}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="d-flex gap-2">
                <Input
                  type="textarea"
                  rows="2"
                  placeholder="Type your reply..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={sending}
                  style={{ resize: 'none' }}
                />
                <Button 
                  color="primary" 
                  onClick={sendReply}
                  disabled={sending || !newMessage.trim()}
                  style={{ minWidth: '50px' }}
                >
                  {sending ? <Spinner size="sm" /> : <AiOutlineSend size={18} />}
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FeedbackChat;